<script>
  import { onMount } from 'svelte';
  import { showAlert } from '../lib/stores.js';
  import { authFetch } from '../lib/api.js';
  import { userRole } from '../lib/stores.js';
  
  let loading = true;
  let error = null;
  let categories = [];
  let showCreateModal = false;
  let showEditModal = false;
  let showDownloadOptionsModal = false;
  let selectedCategory = null;
  let categoryToDownload = null;
  let selectedFormat = 'png';
  let titles = [];
  
  // Form data
  let newCategory = {
    titleId: '',
    nominal: '',
    amount: '',
    codePrefix: '',
    image: null,
    posX: 50,
    posY: 50,
    qrSize: 100,
    showCodeText: true
  };
  
  let editCategory = {
    id: '',
    titleId: '',
    nominal: '',
    amount: '',
    codePrefix: '',
    image: null,
    posX: 50,
    posY: 50,
    qrSize: 100,
    showCodeText: true,
    currentImagePath: ''
  };
  
  let imagePreview = null;
  let editImagePreview = null;
  
  // Load categories and titles
  async function fetchData() {
    try {
      loading = true;
      error = null;
      
      // Fetch all titles
      const titlesResponse = await authFetch('/api/vouchers/titles');
      if (!titlesResponse.success) {
        throw new Error(titlesResponse.message || 'Failed to load titles');
      }
      
      titles = titlesResponse.titles;
      
      // Fetch all categories
      const categoriesResponse = await authFetch('/api/vouchers/categories/0');
      if (!categoriesResponse.success) {
        throw new Error(categoriesResponse.message || 'Failed to load categories');
      }
      
      categories = categoriesResponse.categories;
    } catch (err) {
      console.error('Error loading data:', err);
      error = 'Failed to load data. Please try again.';
      showAlert('error', error);
    } finally {
      loading = false;
    }
  }
  
  // Handle image selection for new category
  function handleImageChange(event) {
    const file = event.target.files[0];
    
    if (file) {
      newCategory.image = file;
      
      // Create preview
      const reader = new FileReader();
      reader.onload = e => {
        imagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }
  
  // Handle image selection for edit category
  function handleEditImageChange(event) {
    const file = event.target.files[0];
    
    if (file) {
      editCategory.image = file;
      
      // Create preview
      const reader = new FileReader();
      reader.onload = e => {
        editImagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }
  
  // Create new category
  async function createCategory() {
    // Validate inputs
    if (!newCategory.titleId || !newCategory.nominal || !newCategory.amount || !newCategory.codePrefix || !newCategory.image) {
      showAlert('error', 'Semua field wajib diisi');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('titleId', newCategory.titleId);
      formData.append('nominal', newCategory.nominal);
      formData.append('amount', newCategory.amount);
      formData.append('codePrefix', newCategory.codePrefix);
      formData.append('posX', newCategory.posX);
      formData.append('posY', newCategory.posY);
      formData.append('qrSize', newCategory.qrSize);
      formData.append('showCodeText', newCategory.showCodeText);
      formData.append('image', newCategory.image);
      
      const response = await fetch('/api/vouchers/categories', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        showAlert('success', 'Kategori voucher berhasil dibuat');
        
        // Reset form
        newCategory = {
          titleId: '',
          nominal: '',
          amount: '',
          codePrefix: '',
          image: null,
          posX: 50,
          posY: 50,
          qrSize: 100,
          showCodeText: true
        };
        imagePreview = null;
        showCreateModal = false;
        
        // Refresh data
        await fetchData();
      } else {
        throw new Error(data.message || 'Failed to create category');
      }
    } catch (err) {
      console.error('Error creating category:', err);
      showAlert('error', 'Gagal membuat kategori voucher');
    }
  }
  
  // Open edit modal and populate form
  function openEditModal(category) {
    selectedCategory = category;
    editCategory = {
      id: category._id,
      titleId: category.titleId._id,
      nominal: category.nominal,
      amount: category.amount,
      codePrefix: category.codePrefix,
      image: null,
      posX: category.posX || 50,
      posY: category.posY || 50,
      qrSize: category.qrSize || 100,
      showCodeText: category.showCodeText !== undefined ? category.showCodeText : true,
      currentImagePath: category.imagePath
    };
    editImagePreview = null;
    showEditModal = true;
  }
  
  // Update category
  async function updateCategory() {
    // Validate inputs
    if (!editCategory.titleId || !editCategory.nominal) {
      showAlert('error', 'Nama dan nominal wajib diisi');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('nominal', editCategory.nominal);
      if (editCategory.amount) formData.append('amount', editCategory.amount);
      if (editCategory.codePrefix) formData.append('codePrefix', editCategory.codePrefix);
      formData.append('posX', editCategory.posX);
      formData.append('posY', editCategory.posY);
      formData.append('qrSize', editCategory.qrSize);
      formData.append('showCodeText', editCategory.showCodeText);
      
      if (editCategory.image) {
        formData.append('image', editCategory.image);
      }
      
      const response = await fetch(`/api/vouchers/categories/${editCategory.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        showAlert('success', 'Kategori voucher berhasil diperbarui');
        showEditModal = false;
        selectedCategory = null;
        
        // Refresh data
        await fetchData();
      } else {
        throw new Error(data.message || 'Failed to update category');
      }
    } catch (err) {
      console.error('Error updating category:', err);
      showAlert('error', 'Gagal memperbarui kategori voucher');
    }
  }
  
  // Delete category
  async function deleteCategory(id) {
    if (!confirm('Apakah Anda yakin ingin menghapus kategori ini? Semua voucher di bawah kategori ini juga akan dihapus.')) {
      return;
    }
    
    try {
      const response = await authFetch(`/api/vouchers/categories/${id}`, {
        method: 'DELETE'
      });
      
      if (response.success) {
        showAlert('success', 'Kategori voucher berhasil dihapus');
        
        // Refresh data
        await fetchData();
      } else {
        throw new Error(response.message || 'Failed to delete category');
      }
    } catch (err) {
      console.error('Error deleting category:', err);
      showAlert('error', 'Gagal menghapus kategori voucher');
    }
  }
  
  // Format currency
  function formatCurrency(value) {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }
  
  // Show download options modal
  function showDownloadOptions(category) {
    categoryToDownload = category;
    selectedFormat = 'png';
    showDownloadOptionsModal = true;
  }
  
  // Bulk download all QR codes for a category
  async function downloadAllQRCodes(categoryId, categoryName, nominal, format = 'png') {
    try {
      // Show loading message
      showAlert('info', 'Sedang memproses pengunduhan QR Code dalam bentuk ZIP...');
      
      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Using fetch directly for blob handling
      const response = await fetch(`/api/export/qrcodes/category/${categoryId}?format=${format}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/zip',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
          throw new Error('Sesi anda telah berakhir. Silakan login kembali.');
        }
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      // Convert the response to a blob
      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('QR Code tidak dapat dihasilkan. File kosong.');
      }
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Set up download link
      const link = document.createElement('a');
      link.href = url;
      
      // Format the filename with sanitized category name and nominal
      const safeName = categoryName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const safeNominal = formatCurrency(nominal).replace(/[^0-9]/g, '');
      const formatExtension = format === 'svg' ? 'svg' : 'png';
      
      link.download = `qr_codes_${safeName}_${safeNominal}_${formatExtension}.zip`;
      
      // Append to document body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Revoke the blob URL to free memory
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
      
      showAlert('success', `Semua QR Code berhasil diunduh dalam format ZIP (format: ${format.toUpperCase()})`);
    } catch (err) {
      console.error('Error downloading QR codes:', err);
      showAlert('error', 'Gagal mengunduh QR Codes: ' + (err.message || 'Unknown error'));
    }
  }
  
  // Proceed with download based on selected format
  function proceedWithDownload() {
    if (categoryToDownload) {
      downloadAllQRCodes(
        categoryToDownload._id, 
        categoryToDownload.titleId.name, 
        categoryToDownload.nominal, 
        selectedFormat
      );
      // Close the modal
      showDownloadOptionsModal = false;
      categoryToDownload = null;
    }
  }
  
  // Initialize
  onMount(() => {
    fetchData();
  });
</script>

<div class="categories-container">
  <div class="page-header">
    <h1>Kategori Voucher</h1>
    
    <div class="header-actions">
      <button class="add-button" on:click={() => showCreateModal = true}>
        + Tambah Kategori
      </button>
    </div>
  </div>
  
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading data...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <p>{error}</p>
      <button on:click={fetchData}>Coba Lagi</button>
    </div>
  {:else if categories.length === 0}
    <div class="empty-state">
      <p>Belum ada kategori voucher. Silakan tambahkan kategori baru.</p>
    </div>
  {:else}
    <div class="categories-grid">
      {#each categories as category (category._id)}
        <div class="category-card">
          <div class="category-image">
            <img src={category.imagePath} alt={`${category.titleId.name} - ${category.nominal}`} />
          </div>
          <div class="category-content">
            <h3>{category.titleId.name}</h3>
            <p class="category-nominal">{formatCurrency(category.nominal)}</p>
            <div class="category-stats">
              <div class="stat-item">
                <span class="stat-label">Total:</span>
                <span class="stat-value">{category.voucherCount}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Terpakai:</span>
                <span class="stat-value">{category.usedCount || 0}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Sisa:</span>
                <span class="stat-value">{category.voucherCount - (category.usedCount || 0)}</span>
              </div>
            </div>
            <div class="category-info">
              <div class="info-item">
                <span class="info-label">Kode Prefix:</span>
                <span class="info-value">{category.codePrefix}</span>
              </div>
              <div class="info-item">
                <span class="info-label">QR Position:</span>
                <span class="info-value">X: {category.posX || 50}%, Y: {category.posY || 50}%</span>
              </div>
              <div class="info-item">
                <span class="info-label">QR Size:</span>
                <span class="info-value">{category.qrSize || 100}px</span>
              </div>
              <div class="info-item">
                <span class="info-label">Show Code Text:</span>
                <span class="info-value">{category.showCodeText !== undefined ? (category.showCodeText ? 'Yes' : 'No') : 'Yes'}</span>
              </div>
            </div>
          </div>
          <div class="category-actions">
            <button class="edit-button" on:click={() => openEditModal(category)}>
              Edit
            </button>
            <button class="delete-button" on:click={() => deleteCategory(category._id)}>
              Hapus
            </button>
            {#if category.voucherCount > 0}
              <button class="download-button" on:click={() => showDownloadOptions(category)}>
                <i class="fas fa-download"></i> Download QR Zip
              </button>
            {/if}
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

<!-- Create Category Modal -->
{#if showCreateModal}
  <div class="modal-backdrop" on:click={() => showCreateModal = false}>
    <div class="modal-content" on:click|stopPropagation>
      <h2>Tambah Kategori Voucher</h2>
      
      <div class="form-group">
        <label for="category-title">Judul Voucher</label>
        <select id="category-title" bind:value={newCategory.titleId}>
          <option value="">Pilih Judul</option>
          {#each titles as title}
            <option value={title._id}>{title.name}</option>
          {/each}
        </select>
      </div>
      
      <div class="form-group">
        <label for="category-nominal">Nominal (Rp)</label>
        <input 
          type="number" 
          id="category-nominal" 
          bind:value={newCategory.nominal} 
          placeholder="Masukkan nominal voucher"
        />
      </div>
      
      <div class="form-group">
        <label for="category-amount">Jumlah Voucher</label>
        <input 
          type="number" 
          id="category-amount" 
          bind:value={newCategory.amount} 
          placeholder="Masukkan jumlah voucher"
        />
      </div>
      
      <div class="form-group">
        <label for="category-prefix">Prefix Kode</label>
        <input 
          type="text" 
          id="category-prefix" 
          bind:value={newCategory.codePrefix} 
          placeholder="Contoh: VC-"
        />
      </div>
      
      <div class="form-group">
        <label for="category-image">Gambar Voucher</label>
        <input 
          type="file" 
          id="category-image" 
          accept="image/*"
          on:change={handleImageChange}
        />
        
        {#if imagePreview}
          <div class="image-preview">
            <img src={imagePreview} alt="Preview" />
          </div>
        {/if}
      </div>
      
      <h3 class="form-section-title">Posisi QR Code</h3>
      <div class="form-row">
        <div class="form-group">
          <label for="category-posX">Posisi X QR Code (%)</label>
          <input 
            type="number" 
            id="category-posX" 
            bind:value={newCategory.posX} 
            min="0"
            max="100"
          />
        </div>
        
        <div class="form-group">
          <label for="category-posY">Posisi Y QR Code (%)</label>
          <input 
            type="number" 
            id="category-posY" 
            bind:value={newCategory.posY}
            min="0"
            max="100"
          />
        </div>
      </div>
      
      <h3 class="form-section-title">Pengaturan QR Code</h3>
      <div class="form-row">
        <div class="form-group">
          <label for="category-qrSize">Ukuran QR Code (px)</label>
          <input 
            type="number" 
            id="category-qrSize" 
            bind:value={newCategory.qrSize}
            min="50"
            max="300"
            placeholder="Default: 100px"
          />
          <div class="form-help">Ukuran default: 100x100px</div>
        </div>
        
        <div class="form-group">
          <label for="category-showCodeText">Tampilkan Teks Kode Voucher</label>
          <select id="category-showCodeText" bind:value={newCategory.showCodeText}>
            <option value={true}>Ya, tampilkan teks</option>
            <option value={false}>Tidak, QR code saja</option>
          </select>
        </div>
      </div>
      
      <div class="modal-actions">
        <button class="cancel-button" on:click={() => showCreateModal = false}>
          Batal
        </button>
        <button class="save-button" on:click={createCategory}>
          Simpan
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Edit Category Modal -->
{#if showDownloadOptionsModal && categoryToDownload}
  <div class="modal-backdrop" on:click={() => showDownloadOptionsModal = false}>
    <div class="modal-content" on:click|stopPropagation>
      <h2>Pilih Format QR Code</h2>
      
      <p class="help-text">Pilih format untuk mengunduh QR code kategori "{categoryToDownload.titleId.name}" ({formatCurrency(categoryToDownload.nominal)})</p>
      
      <div class="format-options">
        <div 
          class="format-option {selectedFormat === 'png' ? 'selected' : ''}" 
          on:click={() => selectedFormat = 'png'}
          on:keydown={e => e.key === 'Enter' && (selectedFormat = 'png')}
          tabindex="0"
          role="button"
          aria-selected={selectedFormat === 'png'}
        >
          <div class="format-option-icon">
            <i class="fas fa-file-image"></i>
          </div>
          <div class="format-option-title">PNG</div>
          <div class="format-option-desc">Format raster yang ideal untuk penggunaan umum</div>
        </div>
        
        <div 
          class="format-option {selectedFormat === 'svg' ? 'selected' : ''}" 
          on:click={() => selectedFormat = 'svg'}
          on:keydown={e => e.key === 'Enter' && (selectedFormat = 'svg')}
          tabindex="0"
          role="button"
          aria-selected={selectedFormat === 'svg'}
        >
          <div class="format-option-icon">
            <i class="fas fa-bezier-curve"></i>
          </div>
          <div class="format-option-title">SVG</div>
          <div class="format-option-desc">Format vektor yang dapat diubah ukurannya tanpa kehilangan kualitas</div>
        </div>
      </div>
      
      <div class="modal-actions">
        <button class="cancel-button" on:click={() => showDownloadOptionsModal = false}>
          Batal
        </button>
        <button class="save-button" on:click={proceedWithDownload}>
          Unduh
        </button>
      </div>
    </div>
  </div>
{/if}

{#if showEditModal && selectedCategory}
  <div class="modal-backdrop" on:click={() => showEditModal = false}>
    <div class="modal-content" on:click|stopPropagation>
      <h2>Edit Kategori Voucher</h2>
      
      <div class="form-group">
        <label for="edit-category-title">Judul Voucher</label>
        <select id="edit-category-title" bind:value={editCategory.titleId} disabled>
          {#each titles as title}
            <option value={title._id}>{title.name}</option>
          {/each}
        </select>
        <p class="help-text">Judul voucher tidak dapat diubah</p>
      </div>
      
      <div class="form-group">
        <label for="edit-category-nominal">Nominal (Rp)</label>
        <input 
          type="number" 
          id="edit-category-nominal" 
          bind:value={editCategory.nominal} 
          placeholder="Masukkan nominal voucher"
        />
      </div>
      
      <div class="form-group">
        <label for="edit-category-amount">Tambah Jumlah Voucher</label>
        <input 
          type="number" 
          id="edit-category-amount" 
          bind:value={editCategory.amount} 
          placeholder="Masukkan jumlah tambahan voucher (opsional)"
        />
        <p class="help-text">Kosongkan jika tidak ingin menambah jumlah voucher</p>
      </div>
      
      <div class="form-group">
        <label for="edit-category-prefix">Prefix Kode (untuk voucher baru)</label>
        <input 
          type="text" 
          id="edit-category-prefix" 
          bind:value={editCategory.codePrefix} 
          placeholder="Contoh: VC-"
        />
        <p class="help-text">Hanya berlaku untuk voucher baru jika menambah jumlah</p>
      </div>
      
      <div class="form-group">
        <label for="edit-category-image">Gambar Voucher</label>
        <input 
          type="file" 
          id="edit-category-image" 
          accept="image/*"
          on:change={handleEditImageChange}
        />
        
        {#if editImagePreview}
          <div class="image-preview">
            <img src={editImagePreview} alt="Preview" />
          </div>
        {:else if editCategory.currentImagePath}
          <div class="image-preview">
            <img src={editCategory.currentImagePath} alt="Current Image" />
            <p class="help-text">Gambar saat ini</p>
          </div>
        {/if}
      </div>
      
      <h3 class="form-section-title">Posisi QR Code</h3>
      <div class="form-row">
        <div class="form-group">
          <label for="edit-category-posX">Posisi X QR Code (%)</label>
          <input 
            type="number" 
            id="edit-category-posX" 
            bind:value={editCategory.posX} 
            min="0"
            max="100"
          />
        </div>
        
        <div class="form-group">
          <label for="edit-category-posY">Posisi Y QR Code (%)</label>
          <input 
            type="number" 
            id="edit-category-posY" 
            bind:value={editCategory.posY}
            min="0"
            max="100"
          />
        </div>
      </div>
      
      <h3 class="form-section-title">Pengaturan QR Code</h3>
      <div class="form-row">
        <div class="form-group">
          <label for="edit-category-qrSize">Ukuran QR Code (px)</label>
          <input 
            type="number" 
            id="edit-category-qrSize" 
            bind:value={editCategory.qrSize}
            min="50"
            max="300"
            placeholder="Default: 100px"
          />
          <div class="form-help">Ukuran default: 100x100px</div>
        </div>
        
        <div class="form-group">
          <label for="edit-category-showCodeText">Tampilkan Teks Kode Voucher</label>
          <select id="edit-category-showCodeText" bind:value={editCategory.showCodeText}>
            <option value={true}>Ya, tampilkan teks</option>
            <option value={false}>Tidak, QR code saja</option>
          </select>
        </div>
      </div>
      
      <div class="modal-actions">
        <button class="cancel-button" on:click={() => showEditModal = false}>
          Batal
        </button>
        <button class="save-button" on:click={updateCategory}>
          Perbarui
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .categories-container {
    padding: 0;
  }
  
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .page-header h1 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #1e3a8a;
    margin: 0;
    letter-spacing: -0.025em;
  }
  
  .header-actions {
    display: flex;
    gap: 0.5rem;
  }
  
  .add-button {
    padding: 0.5rem 1rem;
    background-color: #10b981;
    color: white;
    border: none;
    border-radius: 0.375rem;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .add-button:hover {
    background-color: #059669;
  }
  
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 5rem 0;
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .loading-spinner {
    width: 3rem;
    height: 3rem;
    border: 4px solid #e5e7eb;
    border-top: 4px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1.25rem;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2.5rem;
    background-color: #fee2e2;
    border-radius: 0.5rem;
    color: #b91c1c;
    text-align: center;
  }
  
  .error-container button {
    margin-top: 1rem;
    padding: 0.5rem 1.25rem;
    background-color: #dc2626;
    color: white;
    border: none;
    border-radius: 0.375rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .error-container button:hover {
    background-color: #b91c1c;
  }
  
  .empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 3rem;
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    color: #6b7280;
  }
  
  .categories-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 1.25rem;
  }
  
  .category-card {
    display: flex;
    flex-direction: column;
    background-color: white;
    border-radius: 0.5rem;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s, box-shadow 0.2s;
  }
  
  .category-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
  }
  
  .category-image {
    height: 150px;
    overflow: hidden;
  }
  
  .category-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  
  .category-content {
    flex: 1;
    padding: 1.25rem;
  }
  
  .category-content h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1e3a8a;
    margin: 0 0 0.25rem 0;
  }
  
  .category-nominal {
    font-size: 1.5rem;
    font-weight: 700;
    color: #10b981;
    margin: 0 0 1rem 0;
  }
  
  .category-stats {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
    background-color: #f8fafc;
    padding: 0.75rem;
    border-radius: 0.375rem;
  }
  
  .stat-item {
    display: flex;
    flex-direction: column;
    flex: 1;
    text-align: center;
  }
  
  .stat-label {
    font-size: 0.75rem;
    color: #6b7280;
    margin-bottom: 0.25rem;
  }
  
  .stat-value {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1f2937;
  }
  
  .category-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
  
  .info-item {
    display: flex;
    justify-content: space-between;
    font-size: 0.875rem;
    border-bottom: 1px solid #f3f4f6;
    padding-bottom: 0.5rem;
  }
  
  .info-label {
    color: #6b7280;
  }
  
  .info-value {
    font-weight: 500;
    color: #1f2937;
  }
  
  .category-actions {
    display: flex;
    padding: 0.75rem 1.25rem;
    background-color: #f9fafb;
    gap: 0.5rem;
  }
  
  .edit-button, .delete-button {
    flex: 1;
    padding: 0.5rem;
    border-radius: 0.375rem;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    text-align: center;
  }
  
  .edit-button {
    background-color: #3b82f6;
    color: white;
    border: none;
  }
  
  .edit-button:hover {
    background-color: #2563eb;
  }
  
  .delete-button {
    background-color: #ef4444;
    color: white;
    border: none;
  }
  
  .delete-button:hover {
    background-color: #dc2626;
  }
  
  .download-button {
    flex: 1;
    padding: 0.5rem;
    border-radius: 0.375rem;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    text-align: center;
    background-color: #8b5cf6;
    color: white;
    border: none;
  }
  
  .download-button:hover {
    background-color: #7c3aed;
  }
  
  /* Modals */
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .modal-content {
    background-color: white;
    border-radius: 0.5rem;
    padding: 1.5rem;
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
  }
  
  .modal-content h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e3a8a;
    margin: 0 0 1.5rem 0;
  }
  
  .form-group {
    margin-bottom: 1.25rem;
  }
  
  .form-row {
    display: flex;
    gap: 1rem;
  }
  
  .form-row .form-group {
    flex: 1;
  }
  
  .form-group label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #4b5563;
    margin-bottom: 0.5rem;
  }
  
  .form-group input, .form-group select {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    color: #1f2937;
  }
  
  .form-group input:disabled, .form-group select:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }
  
  .help-text {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.25rem;
  }
  
  .form-help {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.25rem;
  }
  
  .form-section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #4b5563;
    margin: 1.5rem 0 0.75rem 0;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 0.5rem;
  }
  
  .image-preview {
    margin-top: 0.5rem;
    width: 100%;
    max-height: 200px;
    overflow: hidden;
    border-radius: 0.25rem;
    border: 1px solid #e5e7eb;
  }
  
  .image-preview img {
    width: 100%;
    height: auto;
    display: block;
  }
  
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1.5rem;
  }
  
  .save-button, .cancel-button {
    padding: 0.5rem 1rem;
    border-radius: 0.375rem;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .save-button {
    background-color: #3b82f6;
    color: white;
    border: none;
  }
  
  .save-button:hover {
    background-color: #2563eb;
  }
  
  .cancel-button {
    background-color: #f3f4f6;
    color: #4b5563;
    border: 1px solid #d1d5db;
  }
  
  .cancel-button:hover {
    background-color: #e5e7eb;
  }
  
  @media (max-width: 768px) {
    .categories-grid {
      grid-template-columns: 1fr;
    }
    
    .form-row {
      flex-direction: column;
      gap: 0.5rem;
    }
  }
  /* Download Options Modal */
  .format-options {
    display: flex;
    gap: 1rem;
    margin-top: 1rem;
  }
  
  .format-option {
    flex: 1;
    padding: 1rem;
    border: 2px solid #e5e7eb;
    border-radius: 0.5rem;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .format-option:hover {
    border-color: #93c5fd;
    background-color: #f1f5f9;
  }
  
  .format-option.selected {
    border-color: #3b82f6;
    background-color: #eff6ff;
  }
  
  .format-option-icon {
    font-size: 2rem;
    margin-bottom: 0.5rem;
    color: #4b5563;
  }
  
  .selected .format-option-icon {
    color: #3b82f6;
  }
  
  .format-option-title {
    font-weight: 600;
    font-size: 1rem;
    color: #1f2937;
    margin-bottom: 0.25rem;
  }
  
  .format-option-desc {
    font-size: 0.75rem;
    color: #6b7280;
  }
</style>